import { policyReadDone, errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFacility } from "@/lib/facility/facility";
import { useEveryRow } from "@/lib/query/every-row";
import { orpc, orpcClient } from "@/lib/orpc";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { useCan } from "@/lib/policy";
import { NewPolicyDialog } from "./-components/new-policy-dialog";
import { AllPoliciesTable } from "@/routes/_authenticated/_knowledge/policies/-components/all-policies-table";
import { MyPoliciesTable } from "./-components/policy-tables";

export const Route = createFileRoute("/_authenticated/_knowledge/policies/")({
  component: Policies,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** What has been written down, and whether you have read it. */
function Policies() {
  const { facilityId } = useFacility();
  const canManage = useCan("policy:manage");
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const mine = useQuery(orpc.knowledge.policies.mine.queryOptions({ input: { facilityId } }));
  // Every policy, however many: the screen shows them all in one table.
  const all = useEveryRow(
    orpc.knowledge.policies.list.key({ input: { facilityId } }),
    (page, call) => orpcClient.knowledge.policies.list({ facilityId, ...page }, call),
  );

  const acknowledge = useMutation(
    orpc.knowledge.policies.acknowledge.mutationOptions({
      onSuccess: async () => {
        toast.success("Reading recorded");
        await invalidateKeys(queryClient, [orpc.knowledge.policies.key()]);
      },
      onError: fail,
    }),
  );

  const unread = mine.data?.filter((row) => !policyReadDone(row)) ?? [];

  return (
    <>
      <PageHeader
        title="Policies & procedures"
        count={all.data?.rows.length}
        meta="What the organization has written down, and which version is in force."
        actions={
          canManage ? (
            <>
              <Button asChild variant="outline" size="sm">
                <Link to="/policies/access">Manage access</Link>
              </Button>
              {facilityId === "all" ? null : (
                <Button onClick={() => setCreating(true)}>Add policy</Button>
              )}
            </>
          ) : null
        }
      />
      {facilityId === "all" ? null : (
        <NewPolicyDialog open={creating} onOpenChange={setCreating} facilityId={facilityId} />
      )}

      <Tabs defaultValue="mine">
        <TabsList>
          <TabsTrigger value="mine">
            To read{unread.length > 0 ? ` (${unread.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="all">All policies</TabsTrigger>
        </TabsList>

        <TabsContent value="mine">
          {mine.isPending ? (
            <Skeleton className="h-64 w-full" />
          ) : mine.isError ? (
            <ReadFailed
              message="Couldn't load what you have to read."
              error={mine.error}
              onRetry={() => void mine.refetch()}
              retrying={mine.isFetching}
            />
          ) : mine.data && mine.data.length > 0 ? (
            <MyPoliciesTable
              rows={mine.data}
              acknowledging={acknowledge.isPending}
              onAcknowledge={(versionId) => acknowledge.mutate({ versionId })}
            />
          ) : (
            <EmptyState
              title="Nothing to read"
              description="Published policies you are expected to read appear here."
            />
          )}
        </TabsContent>

        <TabsContent value="all">
          {all.isPending ? (
            <Skeleton className="h-64 w-full" />
          ) : all.isError ? (
            <ReadFailed
              message="Couldn't load the policies."
              error={all.error}
              onRetry={() => void all.refetch()}
              retrying={all.isFetching}
            />
          ) : all.data && all.data.rows.length > 0 ? (
            <AllPoliciesTable rows={all.data.rows} />
          ) : (
            <EmptyState
              title="No policies yet"
              description={
                !canManage
                  ? "Policies your organization publishes appear here."
                  : facilityId === "all"
                    ? "Choose a building in the switcher at the top to add the first one."
                    : "Add the first one and publish it when it is ready."
              }
              action={
                canManage && facilityId !== "all" ? (
                  <Button onClick={() => setCreating(true)}>Add policy</Button>
                ) : undefined
              }
            />
          )}
        </TabsContent>
      </Tabs>
    </>
  );
}
